import { Box, Typography } from '@mui/material';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import React from 'react';
import type { NavigationItem } from './types';
import {
  navBadgeSx,
  navIconSx,
  navIconWrapSx,
  navItemSx,
  navLabelCollapsedSx,
  navLabelSx,
} from './styles';

type NavItemProps = {
  item: NavigationItem;
  isCollapsed: boolean;
  isActive: boolean;
  isMobileDialog: boolean;
  isDisabled: boolean;
  onNavigationClick?: () => void;
};

export const NavItem: React.FC<NavItemProps> = ({
  item,
  isCollapsed,
  isActive,
  isMobileDialog,
  isDisabled,
  onNavigationClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const content = (
    <>
      <Box
        component="span"
        className="nav-icon-wrap"
        sx={navIconWrapSx({ isActive, isMobileDialog })}
      >
        <Box
          component="span"
          className="nav-icon"
          sx={navIconSx({ isActive, isMobileDialog })}
        >
          {item.icon}
        </Box>
      </Box>
      {!isCollapsed && (
        <Typography
          component="span"
          className="nav-label"
          sx={navLabelSx({ isActive })}
        >
          {item.label}
        </Typography>
      )}
      {isCollapsed && (
        <Typography
          component="span"
          className="nav-label"
          sx={navLabelCollapsedSx({ isActive })}
        >
          {item.sidebarLabel ?? item.label}
        </Typography>
      )}
      {isMobileDialog && isDisabled && !isCollapsed && (
        <Typography component="span" sx={navBadgeSx}>
          Desktop only
        </Typography>
      )}
    </>
  );

  const handleClick = (e?: React.MouseEvent) => {
    if (isDisabled) {
      if (e) e.preventDefault();
      return;
    }
    if (
      e &&
      !item.isExternal &&
      !e.defaultPrevented &&
      e.button === 0 &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.shiftKey &&
      !e.altKey &&
      location.pathname === '/find-asset' &&
      item.to !== '/find-asset'
    ) {
      e.preventDefault();
      navigate({ to: item.to, search: {} });
    }
    onNavigationClick?.();
  };

  if (item.isExternal) {
    return (
      <Box
        component="a"
        href={item.to}
        target="_blank"
        rel="noopener noreferrer"
        sx={navItemSx({ isCollapsed, isActive, isMobileDialog, isDisabled })}
        title={isCollapsed ? item.label : undefined}
        onClick={handleClick}
        aria-disabled={isDisabled || undefined}
        tabIndex={isDisabled ? -1 : undefined}
      >
        {content}
      </Box>
    );
  }

  return (
    <Box
      component={Link}
      to={item.to}
      sx={navItemSx({ isCollapsed, isActive, isMobileDialog, isDisabled })}
      title={isCollapsed ? item.label : undefined}
      onClick={handleClick}
      aria-disabled={isDisabled || undefined}
    >
      {content}
    </Box>
  );
};
